import React, { useEffect, useState, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Clock,
  Eye,
  CalendarDays,
  CalendarClock,
  ListTodo,
  X,
  AlertCircle,
  PersonStanding,
  PersonStandingIcon,
  ChartAreaIcon,
  RefreshCw,
} from "lucide-react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./app.css";
import Loading from "./loading.js";
import { secretKey } from "./data";
import LeadDetail from "./leadDetail";

const MySwal = withReactContent(Swal2);
const API = "https://apiftpsplit.ftpeople.pl";
const hdrs = { "Content-Type": "application/json", Authorization: secretKey };

const DAYS_PL = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Ndz"];
const MONTHS_PL = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

/* ─── date helpers ─── */
const toKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const todayKey = toKey(new Date());

const getCalendarDays = (year, month) => {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  let startDay = first.getDay();
  startDay = startDay === 0 ? 6 : startDay - 1;

  const days = [];
  for (let i = startDay - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, key: toKey(d), currentMonth: false });
  }
  for (let i = 1; i <= last.getDate(); i++) {
    const d = new Date(year, month, i);
    days.push({ date: d, key: toKey(d), currentMonth: true });
  }
  while (days.length < 42) {
    const d = new Date(
      year,
      month + 1,
      days.length - startDay - last.getDate() + 1
    );
    days.push({ date: d, key: toKey(d), currentMonth: false });
  }
  if (days.length > 35 && !days.slice(35).some((d) => d.currentMonth)) {
    days.length = 35;
  }
  return days;
};

/* ═══════════════════════════════════════════════════════════ */

const TaskCalendar = (props) => {
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("calendar");
  const [taskFilter, setTaskFilter] = useState("PENDING");
  const [typeFilter, setTypeFilter] = useState("all");
  const [respFilter, setRespFilter] = useState(props.user.name);
  const [tasks, setTasks] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [selectedDay, setSelectedDay] = useState(todayKey);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [showLeadDetailId, setShowLeadDetailId] = useState("");
  const [expandTasks, setExpandTasks] = useState(true);
  const [expandMeetings, setExpandMeetings] = useState(true);

  /* ── fetch ── */
  const fetchTasks = async (filter) => {
    setLoading(true);
    try {
      const endpoint =
        filter === "PENDING" ? "getTasksPending" : "getTasksFinished";
      const res = await fetch(`${API}/${endpoint}`, {
        method: "POST",
        headers: hdrs,
        body: "{}",
      });
      const data = await res.json();
      setTasks(data);
    } catch (err) {
      console.error("fetchTasks:", err);
    }
    setLoading(false);
  };

  const acceptTask = async (id, leadId) => {
    const result = await MySwal.fire({
      title: "Wykonać zadanie?",
      text: "Czy na pewno chcesz oznaczyć jako wykonane?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Tak, wykonaj",
      cancelButtonText: "Anuluj",
    });

    if (!result.isConfirmed) return;

    try {
      await fetch(`${API}/acceptTask`, {
        method: "POST",
        headers: hdrs,
        body: JSON.stringify({
          id,
          userId: props.user.id,
          userName: props.user.name,
        }),
      });
      MySwal.fire({ icon: "success", title: "Zadanie", text: "wykonane" });
      fetchTasks(taskFilter);
      if (leadId) {
        setShowLeadDetailId({ id: +leadId });
        setShowLeadDetail(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchTasks(taskFilter);
  }, [taskFilter]);

  /* ── filtered tasks ── */
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      const matchType =
        typeFilter === "all" || (t.type || "zwykłe") === typeFilter;
      const matchResp =
        respFilter === "all" ||
        t.userName === respFilter ||
        t.responsible === "all";
      return matchType && matchResp;
    });
  }, [tasks, typeFilter, respFilter]);

  /* ── calendar data ── */
  const calendarDays = useMemo(
    () => getCalendarDays(year, month),
    [year, month]
  );

  const tasksByDate = useMemo(() => {
    const map = {};
    filteredTasks.forEach((t) => {
      const key = t.date ? t.date.substring(0, 10) : "";
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [filteredTasks]);

  const selectedTasks = tasksByDate[selectedDay] || [];
  const dayMeetings = selectedTasks.filter((t) => t.type === "spotkanie");
  const dayTaskItems = selectedTasks.filter(
    (t) => (t.type || "zwykłe") === "zwykłe"
  );

  /* ── stats ── */
  const totalTasks = filteredTasks.filter(
    (t) => (t.type || "zwykłe") === "zwykłe"
  ).length;
  const totalMeetings = filteredTasks.filter(
    (t) => t.type === "spotkanie"
  ).length;
  const todayTasks = tasksByDate[todayKey]?.length || 0;
  const overdue = filteredTasks.filter((t) => {
    if (taskFilter !== "PENDING") return false;
    return t.date && t.date.substring(0, 10) < todayKey;
  }).length;

  /* ── nav ── */
  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else setMonth(month + 1);
  };
  const goToday = () => {
    setYear(new Date().getFullYear());
    setMonth(new Date().getMonth());
    setSelectedDay(todayKey);
  };

  /* ── lead detail ── */
  if (showLeadDetail) {
    return (
      <LeadDetail
        close={() => {
          setShowLeadDetail(false);
          setShowLeadDetailId("");
        }}
        lead={showLeadDetailId}
        user={props.user}
      />
    );
  }

  if (loading) return <Loading />;

  return (
    <div className="tc-wrapper">
      {/* ── Stats bar ── */}
      <div className="tc-stats">
        <div className="tc-stat">
          <ListTodo size={18} />
          <div>
            <span className="tc-stat-num">{totalTasks}</span>
            <span className="tc-stat-label">
              {taskFilter === "PENDING" ? "zadań" : "wykonanych zadań"}
            </span>
          </div>
        </div>
        <div className="tc-stat">
          <CalendarClock size={18} />
          <div>
            <span className="tc-stat-num">{totalMeetings}</span>
            <span className="tc-stat-label">
              {taskFilter === "PENDING" ? "spotkań" : "odbytych spotkań"}
            </span>
          </div>
        </div>
        <div className="tc-stat">
          <CalendarDays size={18} />
          <div>
            <span className="tc-stat-num">{todayTasks}</span>
            <span className="tc-stat-label">dzisiaj</span>
          </div>
        </div>
        {taskFilter === "PENDING" && (
          <div className={`tc-stat ${overdue > 0 ? "tc-stat--alert" : ""}`}>
            <AlertCircle size={18} />
            <div>
              <span className="tc-stat-num">{overdue}</span>
              <span className="tc-stat-label">zaległych</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Toolbar ── */}
      <div className="tc-toolbar">
        <div className="tc-toolbar-left">
          <div className="tc-filter-tabs">
            <button
              className={`tc-tab ${
                taskFilter === "PENDING" ? "tc-tab--active" : ""
              }`}
              onClick={() => setTaskFilter("PENDING")}
            >
              <Clock size={14} /> Do wykonania
            </button>
            <button
              className={`tc-tab ${
                taskFilter === "FINISHED" ? "tc-tab--active" : ""
              }`}
              onClick={() => setTaskFilter("FINISHED")}
            >
              <CheckCircle2 size={14} /> Wykonane
            </button>
          </div>
          <div className="tc-filter-tabs">
            <button
              className={`tc-tab ${
                typeFilter === "all" ? "tc-tab--active" : ""
              }`}
              onClick={() => setTypeFilter("all")}
            >
              Wszystko
            </button>
            <button
              className={`tc-tab ${
                typeFilter === "zwykłe" ? "tc-tab--active" : ""
              }`}
              onClick={() => setTypeFilter("zwykłe")}
            >
              <ListTodo size={14} /> Zadania
            </button>
            <button
              className={`tc-tab ${
                typeFilter === "spotkanie" ? "tc-tab--active" : ""
              }`}
              onClick={() => setTypeFilter("spotkanie")}
            >
              <CalendarClock size={14} /> Spotkania
            </button>
          </div>
          <div className="tc-filter-tabs">
            <button
              className={`tc-tab ${
                respFilter === "all" ? "tc-tab--active" : ""
              }`}
              onClick={() => setRespFilter("all")}
            >
              Wszystkie
            </button>
            <button
              className={`tc-tab ${
                respFilter === props.user.name ? "tc-tab--active" : ""
              }`}
              onClick={() => setRespFilter(props.user.name)}
            >
              <PersonStandingIcon size={14} /> Tylko moje
            </button>
          </div>
        </div>
        <div className="tc-toolbar-right">
          <div className="tc-view-tabs">
            <button
              className={`tc-view-btn ${
                viewMode === "calendar" ? "tc-view-btn--active" : ""
              }`}
              onClick={() => setViewMode("calendar")}
              title="Kalendarz"
            >
              <CalendarDays size={16} />
            </button>
            <button
              className={`tc-view-btn ${
                viewMode === "list" ? "tc-view-btn--active" : ""
              }`}
              onClick={() => setViewMode("list")}
              title="Lista"
            >
              <ListTodo size={16} />
            </button>
          </div>
          <button
            className="tc-nav-btn"
            onClick={() => fetchTasks(taskFilter)}
            title="Odśwież"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {viewMode === "calendar" ? (
        <div className="tc-calendar-layout">
          {/* ── Calendar grid ── */}
          <div className="tc-calendar">
            <div className="tc-month-nav">
              <button className="tc-nav-btn" onClick={prevMonth}>
                <ChevronLeft size={18} />
              </button>
              <div className="tc-month-label">
                <span className="tc-month-name">{MONTHS_PL[month]}</span>
                <span className="tc-month-year">{year}</span>
              </div>
              <button className="tc-nav-btn" onClick={nextMonth}>
                <ChevronRight size={18} />
              </button>
              <button className="tc-today-btn" onClick={goToday}>
                Dziś
              </button>
            </div>

            <div className="tc-grid tc-grid--header">
              {DAYS_PL.map((d) => (
                <div key={d} className="tc-day-header">
                  {d}
                </div>
              ))}
            </div>

            <div className="tc-grid tc-grid--days">
              {calendarDays.map((day) => {
                const dt = tasksByDate[day.key] || [];
                const hasMeeting = dt.some((t) => t.type === "spotkanie");
                const hasTask = dt.some(
                  (t) => (t.type || "zwykłe") === "zwykłe"
                );
                const isToday = day.key === todayKey;
                const isSelected = day.key === selectedDay;
                const isOverdue =
                  taskFilter === "PENDING" &&
                  day.key < todayKey &&
                  dt.length > 0;
                return (
                  <div
                    key={day.key}
                    className={[
                      "tc-day",
                      !day.currentMonth && "tc-day--other",
                      isToday && "tc-day--today",
                      isSelected && "tc-day--selected",
                      isOverdue && "tc-day--overdue",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => setSelectedDay(day.key)}
                  >
                    <span className="tc-day-num">{day.date.getDate()}</span>
                    {dt.length > 0 && (
                      <div className="tc-day-dots">
                        {hasMeeting && (
                          <span
                            className="tc-dot tc-dot--spotkanie"
                            title="Spotkanie"
                          />
                        )}
                        {hasTask && (
                          <span
                            className="tc-dot tc-dot--task"
                            title="Zadanie"
                          />
                        )}
                        {dt.length > 1 && (
                          <span className="tc-dot-more">{dt.length}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Day detail panel ── */}
          <div className="tc-detail">
            <div className="tc-detail-header">
              <h3 className="tc-detail-title">
                {selectedDay === todayKey
                  ? "Dzisiaj"
                  : new Date(selectedDay + "T00:00:00").toLocaleDateString(
                      "pl-PL",
                      {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      }
                    )}
              </h3>
              <span className="tc-detail-count">
                {selectedTasks.length}{" "}
                {selectedTasks.length === 1 ? "pozycja" : "pozycji"}
              </span>
            </div>

            <div className="tc-detail-list">
              {selectedTasks.length === 0 ? (
                <div className="tc-detail-empty">
                  <CalendarDays size={32} strokeWidth={1} />
                  <p>Brak zadań na ten dzień</p>
                </div>
              ) : (
                <>
                  {/* Spotkania */}
                  {dayMeetings.length > 0 && (
                    <div className="tc-section">
                      <button
                        className="tc-section-header"
                        onClick={() => setExpandMeetings((p) => !p)}
                      >
                        <div className="tc-section-left">
                          <CalendarClock size={15} />
                          <span>Spotkania</span>
                          <span className="tc-section-count">
                            {dayMeetings.length}
                          </span>
                        </div>
                        <ChevronDown
                          size={16}
                          className={`tc-section-chevron ${
                            expandMeetings ? "tc-section-chevron--open" : ""
                          }`}
                        />
                      </button>
                      {expandMeetings && (
                        <div className="tc-section-body">
                          {dayMeetings
                            .sort((a, b) =>
                              (a.hour || "").localeCompare(b.hour || "")
                            )
                            .map((t) => (
                              <TaskCard
                                key={t.id}
                                t={t}
                                taskFilter={taskFilter}
                                acceptTask={acceptTask}
                                onLeadClick={() => {
                                  setShowLeadDetailId({ id: +t.leadId });
                                  setShowLeadDetail(true);
                                }}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Zadania */}
                  {dayTaskItems.length > 0 && (
                    <div className="tc-section">
                      <button
                        className="tc-section-header"
                        onClick={() => setExpandTasks((p) => !p)}
                      >
                        <div className="tc-section-left">
                          <ListTodo size={15} />
                          <span>Zadania</span>
                          <span className="tc-section-count">
                            {dayTaskItems.length}
                          </span>
                        </div>
                        <ChevronDown
                          size={16}
                          className={`tc-section-chevron ${
                            expandTasks ? "tc-section-chevron--open" : ""
                          }`}
                        />
                      </button>
                      {expandTasks && (
                        <div className="tc-section-body">
                          {dayTaskItems.map((t) => (
                            <TaskCard
                              key={t.id}
                              t={t}
                              taskFilter={taskFilter}
                              acceptTask={acceptTask}
                              onLeadClick={() => {
                                setShowLeadDetailId({ id: +t.leadId });
                                setShowLeadDetail(true);
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── LIST VIEW ── */
        <div className="tc-list">
          <div className="lq-table-wrap db-table-scroll">
            <table className="lq-table">
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Zadanie</th>
                  <th>Klient</th>
                  <th>Telefon</th>
                  <th>E-mail</th>
                  <th>Data</th>
                  <th>Godzina</th>
                  <th>Odpowiedzialny</th>
                  <th>Akcja</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks
                  .sort((a, b) => {
                    const dc = new Date(b.date) - new Date(a.date);
                    if (dc !== 0) return dc;
                    if (a.hour && b.hour) return a.hour.localeCompare(b.hour);
                    return 0;
                  })
                  .map((t) => {
                    const isMeeting = t.type === "spotkanie";
                    return (
                      <tr
                        key={t.id}
                        style={
                          t.task.includes("rozmowa sprzeda")
                            ? { backgroundColor: "#ffa50024" }
                            : {}
                        }
                      >
                        <td>
                          <span
                            className={`tc-task-type-badge ${
                              isMeeting
                                ? "tc-task-type-badge--spotkanie"
                                : "tc-task-type-badge--task"
                            }`}
                          >
                            {isMeeting ? (
                              <>
                                <CalendarClock size={12} /> Spotkanie
                              </>
                            ) : (
                              <>
                                <ListTodo size={12} /> Zadanie
                              </>
                            )}
                          </span>
                        </td>
                        <td>
                          {t.task} ? |{" "}
                          {t.company === "SPRZEDAZ" ? t.subCompany : t.company}
                        </td>
                        <td>{t.leadName}</td>
                        <td className="lq-td-mono">{t.phone}</td>
                        <td className="lq-td-mono">{t.mail}</td>
                        <td className="lq-td-mono">
                          <span
                            className={
                              t.date?.substring(0, 10) < todayKey &&
                              taskFilter === "PENDING"
                                ? "tc-overdue-text"
                                : ""
                            }
                          >
                            {t.date}
                          </span>
                        </td>
                        <td className="lq-td-mono">
                          {isMeeting && t.hour ? t.hour : "—"}
                        </td>
                        <td>{t.userName}</td>
                        <td>
                          <div className="tc-list-actions">
                            {taskFilter === "PENDING" && (
                              <button
                                className="tc-action-btn tc-action-btn--accept"
                                onClick={() => acceptTask(t.id, t.leadId)}
                              >
                                <CheckCircle2 size={14} /> Wykonaj
                              </button>
                            )}
                            {isMeeting && (
                              <button
                                className="tc-action-btn tc-action-btn--view"
                                onClick={() => {
                                  setShowLeadDetailId({ id: +t.leadId });
                                  setShowLeadDetail(true);
                                }}
                              >
                                <ChartAreaIcon size={14} /> Przygotuj się
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                {filteredTasks.length === 0 && (
                  <tr>
                    <td colSpan={9} className="lq-empty-row">
                      Brak zadań
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── TaskCard — POPRAWIONY ── */
const TaskCard = ({ t, taskFilter, acceptTask, onLeadClick }) => {
  const isMeeting = t.type === "spotkanie";
  return (
    <div
      className={`tc-task-card ${isMeeting ? "tc-task-card--spotkanie" : ""}`}
      style={
        t.task.includes("rozmowa sprzeda")
          ? { backgroundColor: "#ffa50024" }
          : {}
      }
    >
      <div className="tc-task-body">
        {isMeeting && t.hour && <span className="tc-task-hour">{t.hour}</span>}
        <p className="tc-task-name">
          {t.task} | {t.company === "SPRZEDAZ" ? t.subCompany : t.company}
        </p>
        <div className="tc-task-meta">
          <span>{t.leadName}</span>
          <span>{t.phone}</span>
          {t.mail && <span>{t.mail}</span>}
        </div>
        <div className="tc-task-user">{t.userName}</div>
      </div>
      <div className="tc-task-actions">
        {taskFilter === "PENDING" && (
          <button
            className="tc-action-btn tc-action-btn--accept"
            onClick={() => acceptTask(t.id, t.leadId)}
          >
            <CheckCircle2 size={14} /> Wykonaj
          </button>
        )}

        {isMeeting && (
          <button
            className="tc-action-btn tc-action-btn--view"
            onClick={onLeadClick}
          >
            <ChartAreaIcon size={14} /> Przygotuj się
          </button>
        )}
      </div>
    </div>
  );
};

export default TaskCalendar;
